import pluralize from "pluralize";
import { flatten } from "ramda";
import { type FC, type JSX, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "widgets";

import { getSequenceFeatureTypeDisplayName, type SEQUENCING_TYPE } from "@/settings/settings.ts";

import { getTablePageTips } from "../../../app-utils/help.js";
import { toTitleCase } from "../../../app-utils/string.js";
import { type InputTableQuerySpec, type Project, type SequenceFeatureTypes } from "../../../graphql.js";
import { createUniqueKeyFromTableSpec, selectTable } from "../../../redux/modules/table-data.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import PageHeader from "../navigation-panel/PageHeader.js";
import styleLayout from "../styles/PageLayout.module.scss";
import TipTour from "../TipTour.js";
import DataTableContainer from "./DataTableContainer.js";

interface IGenericDataTableContainerOwnProps {
  title?: string;
  subtitle?: string;
  tableSpec?: InputTableQuerySpec;
  middleToolbarElement?: React.ReactNode;
  rightToolbarElement?: React.ReactNode;
  viewId?: number;
  hideCheckboxes?: boolean;
  headerAttributes?: JSX.Element[];
  noRowsText?: string;
  genomeSequencingType?: keyof typeof SEQUENCING_TYPE;
  project?: Project;
  headerTags?: JSX.Element[];
}

const GenericDataTableContainer: FC<IGenericDataTableContainerOwnProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const sequenceFeatureTypes = useSelector<IAppState, SequenceFeatureTypes>(
    (state) => state.listing.sequenceFeatureTypes,
  );

  useEffect(() => {
    dispatch(selectTable(props.tableSpec));
  }, []);

  let headerTitle: string;

  if (props.title) {
    headerTitle = props.title;
  } else if (props.tableSpec) {
    headerTitle = toTitleCase(
      pluralize.plural(getSequenceFeatureTypeDisplayName(props.tableSpec, sequenceFeatureTypes)),
    );
  }

  return (
    <TipTour tips={getTablePageTips()}>
      <PageHeader
        title={headerTitle}
        subtitle={props.subtitle}
        toolsWidgets={flatten([props.middleToolbarElement])}
        actionWidgets={flatten([props.rightToolbarElement])}
        widgets={props.headerAttributes}
        project={props.project}
        tags={props.headerTags}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        {props.tableSpec ? (
          <DataTableContainer
            key={createUniqueKeyFromTableSpec(props.tableSpec)}
            tableSpec={props.tableSpec}
            viewId={props.viewId}
            hideCheckboxes={props.hideCheckboxes}
            gridContext={{ tableSpec: props.tableSpec, genomeSequencingType: props.genomeSequencingType }}
            noRowsText={props.noRowsText}
          />
        ) : (
          <LoadingSpinner inCenter />
        )}
      </div>
    </TipTour>
  );
};

export default GenericDataTableContainer;
